import { Feature, Geometry } from "geojson";

import wellknown from "wellknown";
/**
 * Converts a WKT (Well-Known Text) string to GeoJSON Feature
 */
export const wktToGeoJSON = (wkt: string): Feature | null => {
  try {
    const geometry = wellknown.parse(wkt);

    if (!geometry) {
      return null;
    }
    return {
      type: "Feature",
      geometry: geometry as Geometry,
      properties: {}
    };
  } catch (error) {
    console.error("Error converting WKT to GeoJSON:", error);
    return null;
  }
};

/**
 * Converts an array of WKT strings to GeoJSON Features
 */
export const wktArrayToGeoJSON = (wktArray: string[]): Feature[] => {
  return wktArray
    .map((wkt) => wktToGeoJSON(wkt))
    .filter((feature): feature is Feature => feature !== null);
};

/**
 * Converts a GeoJSON Feature or Geometry to WKT string
 */
export const geoJSONToWKT = (geoJSON: Feature | Geometry): string | null => {
  try {
    // @ts-expect-error - wellknown types are not fully compatible with @types/geojson
    const wkt = wellknown.stringify(geoJSON);
    return wkt;
  } catch (error) {
    console.error("Error converting GeoJSON to WKT:", error);
    return null;
  }
};

/**
 * Converts an array of GeoJSON Features or Geometries to WKT strings
 */
export const geoJSONArrayToWKT = (
  geoJSONArray: (Feature | Geometry)[]
): string[] => {
  return geoJSONArray
    .map((geojson) => geoJSONToWKT(geojson))
    .filter((wkt): wkt is string => wkt !== null);
};

/**
 * Checks if a WKT string represents a Point geometry
 */
export const isWKTPoint = (wkt: string): boolean => {
  if (typeof wkt !== "string") {
    return false;
  }
  try {
    // Parse the WKT string to GeoJSON
    const geometry = wellknown.parse(wkt);

    if (!geometry) {
      return false;
    }

    // Check if the geometry type is Point
    return geometry.type === "Point";
  } catch (error) {
    console.error("Error parsing WKT:", error);
    return false;
  }
};

/**
 * Checks if a WKT string represents a Polygon or MultiPolygon geometry
 */
export const isWKTPolygon = (wkt: string): boolean => {
  if (typeof wkt !== "string") {
    return false;
  }
  try {
    // Parse the WKT string to GeoJSON
    const geometry = wellknown.parse(wkt);

    if (!geometry) {
      return false;
    }

    // Check if the geometry type is Point
    return geometry.type === "Polygon" || geometry.type === "MultiPolygon";
  } catch (error) {
    console.error("Error parsing WKT:", error);
    return false;
  }
};

export function checkLonLatOrder(coordinates: number[]): boolean {
  if (coordinates.length < 2) {
    return false;
  }
  const [longitude, latitude] = coordinates;

  const isLongitude = longitude >= -180 && longitude <= 180;
  const isLatitude = latitude >= -90 && latitude <= 90;

  // Correct order: [lon, lat]
  if (isLongitude && isLatitude) {
    return true;
  }

  return false;
}

export function walkCoordinates(
  coords: number[] | number[][] | number[][][],
  checker: (coord: number[]) => boolean
): boolean {
  if (
    Array.isArray(coords) &&
    typeof coords[0] === "number" &&
    typeof coords[1] === "number"
  ) {
    return checker(coords as number[]);
  }

  // Recursive case
  if (Array.isArray(coords)) {
    return coords.every((c) =>
      Array.isArray(c) ? walkCoordinates(c, checker) : false
    );
  }

  return false;
}

export const isValidCoorOrderWKT = (wkt: string): boolean => {
  const geometry = wellknown.parse(wkt);

  // Check if geometry is not a GeometryCollection and has coordinates
  if (
    !geometry ||
    (geometry.type === "GeometryCollection" && !("coordinates" in geometry)) ||
    !Array.isArray(geometry.coordinates)
  ) {
    return false;
  }

  return walkCoordinates(geometry.coordinates, (coord) =>
    checkLonLatOrder(coord)
  );
};

export const isValidCoorOrderGeoJSON = (geoJSON: Geometry): boolean => {
  // Check if geometry is not a GeometryCollection and has coordinates
  if (
    !geoJSON ||
    (geoJSON.type === "GeometryCollection" && !("coordinates" in geoJSON)) ||
    !Array.isArray(geoJSON.coordinates)
  ) {
    return false;
  }

  return walkCoordinates(geoJSON.coordinates, (coord) =>
    checkLonLatOrder(coord)
  );
};

export const isGeometryStructure = (input: Geometry): boolean => {
  if (!input || typeof input !== "object") return false;

  const validTypes = [
    "Point",
    "MultiPoint",
    "LineString",
    "MultiLineString",
    "Polygon",
    "MultiPolygon"
  ];

  if (!validTypes.includes(input.type)) return false;

  if (input.type === "GeometryCollection") {
    return false;
  }

  return Array.isArray(input.coordinates);
};
